"use client";

import { useState, useEffect } from "react";
import { useAuth } from '@/contexts/AuthContext';
import { supabase } from '@/utils/supabase/client';
import { useQuery, useMutation } from "@tanstack/react-query";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { ChatMessage } from "@/components/ChatMessage";
import { MessageCircle, Send, Search } from 'lucide-react';
import { showError } from '@/utils/toast';
import { toast } from 'sonner';
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";

interface Message {
  id: string;
  content: string;
  sender_id: string;
  receiver_id: string;
  timestamp: string;
}

const MessagesPage: React.FC = () => {
  const { profile } = useAuth();
  const [messages, setMessages] = useState<Message[]>([]);
  const [newMessage, setNewMessage] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!profile?.id) {
      setLoading(false);
      return;
    }

    const fetchMessages = async () => {
      try {
        setError(null);
        const { data, error: queryError } = await supabase
          .from('messages')
          .select('*')
          .or(`sender_id.eq.${profile.id},receiver_id.eq.${profile.id}`)
          .order('timestamp', { ascending: true });

        if (queryError) {
          console.error('Messages: Query error:', queryError);
          throw queryError;
        }

        setMessages(data || []);
      } catch (err: any) {
        console.error('Messages: Fetch failed:', err);
        setError(`Failed to load messages: ${err.message}`);
      } finally {
        setLoading(false);
      }
    };

    fetchMessages();

    // Realtime subscription
    const channel = supabase
      .channel('messages')
      .on(
        'postgres_changes',
        { event: '*', schema: 'public', table: 'messages', filter: `sender_id=eq.${profile.id},receiver_id=eq.${profile.id}` },
        (payload) => {
          console.log('Messages: Realtime update:', payload.eventType);
          if (payload.eventType === 'INSERT') {
            const newMessageData = payload.new as Message;
            setMessages(prev => [...prev, newMessageData]);
          }
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
      console.log('Messages: Cleanup - unsubscribed');
    };
  }, [profile?.id]);

  const sendMessage = async () => {
    if (!newMessage.trim()) return;

    try {
      const { error } = await supabase
        .from('messages')
        .insert({
          sender_id: profile?.id,
          receiver_id: 'some_other_user_id', // Replace with actual receiver ID
          content: newMessage,
          timestamp: new Date().toISOString(),
        });

      if (error) throw error;

      setNewMessage('');
      toast.success('Message sent!');
    } catch (err: any) {
      console.error('Send message error:', err);
      showError('Failed to send message');
    }
  };

  return (
    <div className="container mx-auto p-4">
      <h1 className="text-3xl font-bold mb-6">Messages</h1>
      <Card>
        <CardHeader>
          <CardTitle>Chat</CardTitle>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div>Loading messages...</div>
          ) : error ? (
            <div className="text-destructive">{error}</div>
          ) : (
            <div className="space-y-4">
              {messages.map((message) => (
                <ChatMessage
                  key={message.id}
                  message={{
                    ...message,
                    sender_name: profile?.first_name || 'You',
                  }}
                  isOwn={message.sender_id === profile?.id}
                />
              ))}
              <div className="flex items-center space-x-2">
                <Input
                  type="text"
                  placeholder="Type your message..."
                  value={newMessage}
                  onChange={(e) => setNewMessage(e.target.value)}
                />
                <Button onClick={sendMessage}>
                  <Send className="h-4 w-4" />
                </Button>
              </div>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default MessagesPage;